import { useState, useEffect } from 'react';
import {Modal, ModalDialog, ModalOverflow, Stack, Typography, FormControl, FormLabel, List, ListItem, ListItemButton, Avatar, Box, Chip, Button, ModalClose, CircularProgress } from '@mui/joy';
import { motion, AnimatePresence } from 'framer-motion';
import BlockRoundedIcon from '@mui/icons-material/BlockRounded';
import { APIBlockChatUser, APIGetChatUsers } from '../../api';


export default function BlockUserModal({ open, onClose, chat, token }) {
    const [chatUsers, setChatUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [blockingUser, setBlockingUser] = useState(false);

    // Загрузка участников чата при открытии модального окна
    useEffect(() => {
        const loadChatUsers = async () => {
            if (open && chat?.chat_id) {
                setLoading(true);
                try {
                    const usersData = await APIGetChatUsers(token, chat.chat_id);
                    // Фильтруем пользователей, исключая текущего и уже заблокированных
                    const filteredUsers = usersData.chat_users?.filter(user => 
                        !user.is_blocked && 
                        user.profile_data?.user_id !== chat.currentUser?.id
                    ) || [];
                    setChatUsers(filteredUsers);
                } catch (error) {
                    console.error('Ошибка загрузки участников чата:', error);
                    alert('Не удалось загрузить список участников');
                } finally {
                    setLoading(false);
                }
            }
        };

        if (open) {
            loadChatUsers();
            setSelectedUser(null);
        }
    }, [open, chat?.chat_id, token]);

    const handleUserSelect = (user) => {
        setSelectedUser(user);
    };

    const handleBlockUser = async () => {
        if (!selectedUser) return;

        // Подтверждение действия
        const userName = selectedUser.user_data?.full_name || selectedUser.profile_data?.name || 'Пользователь';
        const confirmBlock = window.confirm(`Вы уверены, что хотите заблокировать пользователя ${userName}?`);
        
        if (!confirmBlock) return;

        setBlockingUser(true);
        try {
            await APIBlockChatUser(token, chat.chat_id, selectedUser.user_data?.id, {
                blocked_reason: 'Блокировка администратором'
            });
            
            alert(`Пользователь ${userName} успешно заблокирован`);
            onClose();
            
            // Можно добавить обновление состояния чата здесь
        } catch (error) {
            console.error('Ошибка блокировки пользователя:', error);
            alert('Не удалось заблокировать пользователя');
        } finally {
            setBlockingUser(false);
        }
    };

    const getRoleColor = (role) => {
        switch (role) {
            case 'creator': return 'primary';
            case 'admin': return 'success';
            default: return 'neutral';
        }
    };

    const getRoleText = (role) => {
        switch (role) {
            case 'creator': return 'Создатель';
            case 'admin': return 'Администратор';
            default: return 'Участник';
        }
    };

    return (
        <AnimatePresence>
            {open && (
                <Modal
                    sx={{ zIndex: 10003 }}
                    open={open}
                    onClose={onClose}
                    slotProps={{
                        backdrop: {
                            component: motion.div,
                            initial: { opacity: 0, backdropFilter: 'blur(0px)' },
                            animate: { opacity: 1, backdropFilter: 'blur(8px)' },
                            exit: { opacity: 0, backdropFilter: 'blur(0px)' },
                            transition: { duration: 0.3, ease: "easeInOut" },
                        }
                    }}
                >
                    <ModalOverflow sx={{ height: 'auto' }}>
                        <ModalDialog
                            size="md"
                            component={motion.div}
                            initial={{ opacity: 0, y: -10, scale: 0.98 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: 10, scale: 0.98 }}
                            transition={{ duration: 0.2, ease: "easeInOut" }}
                            sx={(theme) => ({
                                [theme.breakpoints.up("xl")]: { width: '450px', mx: 0 },
                                [theme.breakpoints.down("xl")]: { width: '450px', mx: 0 },
                                [theme.breakpoints.down("lg")]: { width: '450px', mx: 0 },
                                [theme.breakpoints.down("md")]: { width: '400px', mx: 0 },
                                [theme.breakpoints.down("sm")]: { width: '95%', p: 3 },
                                width: '100%'
                            })}
                        >
                            <ModalClose onClick={onClose} />
                            
                            <Stack spacing={3}>
                                <Stack direction="row" spacing={2} alignItems="center">
                                    <BlockRoundedIcon sx={{ fontSize: 32, color: 'danger.500' }} />
                                    <div>
                                        <Typography level="h3">Блокировка пользователя</Typography>
                                        <Typography level="body-sm" color="neutral">
                                            Выберите пользователя для блокировки
                                        </Typography>
                                    </div>
                                </Stack>

                                {loading ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                        <CircularProgress />
                                    </Box>
                                ) : chatUsers.length === 0 ? (
                                    <Box sx={{ textAlign: 'center', py: 4 }}>
                                        <Typography level="body-lg" color="neutral">
                                            Нет доступных пользователей для блокировки
                                        </Typography>
                                    </Box>
                                ) : (
                                    <>
                                        <FormControl>
                                            <FormLabel>Участники сообщества</FormLabel>
                                            <List
                                                sx={{
                                                    maxHeight: 300,
                                                    overflow: 'auto',
                                                    borderRadius: 'md',
                                                    border: '1px solid',
                                                    borderColor: 'divider'
                                                }}
                                            >
                                                {chatUsers.map((user) => (
                                                    <ListItem key={user.id}>
                                                        <ListItemButton
                                                            selected={selectedUser?.id === user.id}
                                                            onClick={() => handleUserSelect(user)}
                                                            sx={{
                                                                borderRadius: 'sm',
                                                                '&[aria-selected="true"]': {
                                                                    backgroundColor: 'primary.softBg',
                                                                    border: '2px solid',
                                                                    borderColor: 'primary.500'
                                                                }
                                                            }}
                                                        >
                                                            <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%' }}>
                                                                <Avatar 
                                                                    src={user.profile_data?.photo_file_path}
                                                                    size="sm"
                                                                />
                                                                <Box sx={{ flex: 1 }}>
                                                                    <Typography level="title-sm">
                                                                        {user.user_data?.full_name || user.profile_data?.name || 'Без имени'}
                                                                    </Typography>
                                                                    <Typography level="body-xs" color="neutral">
                                                                        {user.user_data?.username && `@${user.user_data.username}`}
                                                                    </Typography>
                                                                </Box>
                                                                <Chip 
                                                                    size="sm" 
                                                                    variant="soft" 
                                                                    color={getRoleColor(user.role)}
                                                                >
                                                                    {getRoleText(user.role)}
                                                                </Chip>
                                                            </Stack>
                                                        </ListItemButton>
                                                    </ListItem>
                                                ))}
                                            </List>
                                        </FormControl>

                                        {selectedUser && (
                                            <Stack spacing={2}>
                                                <Typography level="body-sm" color="neutral">
                                                    Выбран: <strong>{selectedUser.user_data?.full_name || selectedUser.profile_data?.name}</strong>
                                                    {selectedUser.role === 'creator' && (
                                                        <Typography level="body-sm" color="warning" sx={{ ml: 1 }}>
                                                            (Нельзя заблокировать создателя)
                                                        </Typography>
                                                    )}
                                                </Typography>
                                                
                                                <Button
                                                    variant="solid"
                                                    color="danger"
                                                    loading={blockingUser}
                                                    onClick={handleBlockUser}
                                                    disabled={selectedUser.role === 'creator'}
                                                    startDecorator={<BlockRoundedIcon />}
                                                    sx={{ borderRadius: 'lg' }}
                                                >
                                                    Заблокировать пользователя
                                                </Button>
                                                
                                                {selectedUser.role === 'creator' && (
                                                    <Typography level="body-xs" color="warning">
                                                        Создателя сообщества нельзя заблокировать
                                                    </Typography>
                                                )}
                                            </Stack>
                                        )}
                                    </>
                                )}

                                <Button
                                    variant="outlined"
                                    color="neutral"
                                    onClick={onClose}
                                    sx={{ borderRadius: 'lg' }}
                                >
                                    Отмена
                                </Button>
                            </Stack>
                        </ModalDialog>
                    </ModalOverflow>
                </Modal>
            )}
        </AnimatePresence>
    );
}